// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useChangePasswordMutation } from "@/api/hooks/use-change-password";
import { useProfileQuery } from "@/api/hooks/use-profile";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { PasskeyManager } from "@/features/iam/passkey-manager";
import { TwoFactorSection } from "@/features/profile/two-factor-section";
import { AuthError } from "@/lib/auth/auth-client";

export function ProfileSecurityTab() {
  const { t } = useTranslation("back-office");
  const { data: realm, isLoading } = useProfileQuery((p) => p.realm);

  if (isLoading) {
    return <InlineSpinner className="py-16" />;
  }

  // SSO accounts authenticate through their IdP — password, passkeys and 2FA don't apply here
  // (and the backend rejects them), so surface an explanation instead of dead-end controls.
  if (realm && realm !== "local") {
    return (
      <div className="max-w-2xl p-6">
        <div className="flex items-start gap-3 rounded-md border bg-muted/40 p-4 text-sm">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="space-y-1">
            <p className="font-medium text-foreground">{t("profile.security.sso.title")}</p>
            <p className="text-muted-foreground">{t("profile.security.sso.description")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <ChangePasswordSection />
      <Separator />
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">{t("passkeys.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("passkeys.description")}</p>
        </div>
        <PasskeyManager />
      </section>
      <Separator />
      <TwoFactorSection />
    </div>
  );
}

function ChangePasswordSection() {
  const { t } = useTranslation("back-office");
  const changePassword = useChangePasswordMutation();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const schema = z
    .object({
      currentPassword: z.string().min(1, t("profile.security.password.required")),
      newPassword: z.string().min(8, t("profile.security.password.tooShort")),
      confirmPassword: z.string().min(1, t("profile.security.password.required")),
    })
    .refine((values) => values.newPassword === values.confirmPassword, {
      path: ["confirmPassword"],
      message: t("profile.security.password.mismatch"),
    });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  async function onSubmit(values: z.infer<typeof schema>) {
    try {
      await changePassword.mutateAsync({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      form.reset();
      setShowCurrent(false);
      setShowNew(false);
      toast.success(t("profile.security.password.success"));
    } catch (err) {
      // The backend returns 403 for a wrong current password or an SSO account that has no password.
      const message =
        err instanceof AuthError && err.status === 403
          ? t("profile.security.password.invalid")
          : t("profile.security.password.error");
      toast.error(message);
    }
  }

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">{t("profile.security.password.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("profile.security.password.description")}</p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("profile.security.password.current")}</FormLabel>
                <FormControl>
                  <PasswordInput
                    field={field}
                    autoComplete="current-password"
                    show={showCurrent}
                    onToggle={() => setShowCurrent((prev) => !prev)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("profile.security.password.new")}</FormLabel>
                <FormControl>
                  <PasswordInput
                    field={field}
                    autoComplete="new-password"
                    show={showNew}
                    onToggle={() => setShowNew((prev) => !prev)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("profile.security.password.confirm")}</FormLabel>
                <FormControl>
                  <PasswordInput field={field} autoComplete="new-password" show={showNew} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={changePassword.isPending} className="gap-2">
            {changePassword.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {changePassword.isPending
              ? t("profile.security.password.submitting")
              : t("profile.security.password.submit")}
          </Button>
        </form>
      </Form>
    </section>
  );
}

interface PasswordInputProps {
  // The react-hook-form field bag; typed loosely to keep this helper field-agnostic.
  field: React.InputHTMLAttributes<HTMLInputElement>;
  autoComplete: string;
  show: boolean;
  onToggle?: () => void;
}

function PasswordInput({ field, autoComplete, show, onToggle }: PasswordInputProps) {
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        autoComplete={autoComplete}
        data-1p-ignore
        data-lpignore="true"
        className={onToggle ? "pr-9" : undefined}
        {...field}
      />
      {onToggle && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          onClick={onToggle}
          tabIndex={-1}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      )}
    </div>
  );
}
